import { useParams } from "react-router-dom";
import { useEffect } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Clock,
  Share2,
  Linkedin,
  Twitter,
  Link as LinkIcon,
} from "lucide-react";
import SEO from "./shiftlog/SEO";
import ArticleBlocks from "./shiftlog/ArticleBlocks";
import { articles, getArticleBySlug } from "./data/shiftlogArticles";

/**
 * ShiftlogArticlePage — full article page rendered inside the site shell.
 * The site's existing Header/Footer/Layout should wrap this via App routing.
 * All visual styling is scoped under .sl-scope so the surrounding site
 * (homepage, navigation, pricing, etc.) is unaffected.
 */
const ShiftlogArticlePage = () => {
const params = useParams();
const slug = (params?.slug || window.location.pathname.split("/").filter(Boolean).pop() || "").trim();

const navigate = (url) => {
  window.location.href = url;
};

const article = getArticleBySlug(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="sl-scope" data-testid="article-not-found">
        <section className="sl-notfound">
          <div className="sl-notfound__inner">
            <div className="sl-notfound__code">404</div>
            <h1 className="sl-notfound__title">Article not found</h1>
            <p className="sl-notfound__text">
              The article you’re looking for doesn’t exist or has been moved.
            </p>
            <button
              onClick={() => navigate("/resources")}
              className="sl-btn sl-btn--primary"
              data-testid="notfound-back-button"
            >
              Back to Resources
            </button>
          </div>
        </section>
      </div>
    );
  }

  const canonical = `https://jarekborowiec.com/resources/${article.slug}`;
  const shareUrl = encodeURIComponent(canonical);
  const shareText = encodeURIComponent(article.title);

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(canonical);
    } catch {
      // noop
    }
  };

  return (
    <div className="sl-scope" data-testid="article-page">
      <SEO
        title={`${article.title} | Jarek Borowiec`}
        description={article.excerpt}
        keywords={article.keywords}
        canonical={canonical}
        ogType="article"
        publishedTime={article.date}
      />

      <article className="sl-article">
        <div className="sl-article__head">
          <a
            to="/resources"
            className="sl-article__back"
            data-testid="article-back-link"
          >
            <ArrowLeft size={15} strokeWidth={2.4} />
            All resources
          </a>

          <div className="sl-article__category">{article.category}</div>

          <h1 className="sl-article__title" data-testid="article-title">
            {article.title}
          </h1>

          <div className="sl-article__meta">
            <span className="sl-article__meta-item">
              <Calendar size={14} strokeWidth={2.2} />
              {article.dateDisplay}
            </span>
            <span className="sl-article__meta-item">
              <Clock size={14} strokeWidth={2.2} />
              {article.readTime}
            </span>
            <span className="sl-article__meta-item sl-article__meta-item--author">
              By Jarek Borowiec
            </span>
          </div>

          <div
            className={`sl-article__hero-bar sl-article__hero-bar--${article.accent}`}
          />
        </div>

        <div className="sl-article__container">
          <ArticleBlocks blocks={article.blocks} />

          <div className="sl-article__share" data-testid="article-share">
            <div className="sl-article__share-label">
              <Share2 size={14} strokeWidth={2.2} />
              Share this article
            </div>
            <div className="sl-article__share-actions">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="sl-article__share-btn"
                aria-label="Share on LinkedIn"
                data-testid="share-linkedin"
              >
                <Linkedin size={15} strokeWidth={2.2} />
                LinkedIn
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="sl-article__share-btn"
                aria-label="Share on X"
                data-testid="share-twitter"
              >
                <Twitter size={15} strokeWidth={2.2} />
                X / Twitter
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className="sl-article__share-btn"
                aria-label="Copy link"
                data-testid="share-copy-link"
              >
                <LinkIcon size={15} strokeWidth={2.2} />
                Copy link
              </button>
            </div>
          </div>
        </div>

        <section className="sl-article-cta" data-testid="article-cta">
          <div className="sl-article-cta__inner">
            <div className="sl-article-cta__kicker">Ready to see it in action?</div>
            <h2 className="sl-article-cta__title">
              See how ShiftLog handles shift handovers<br />
              in under 30 seconds.
            </h2>
            <p className="sl-article-cta__text">
              A live, interactive demo — no signup required. Log an issue,
              update it, hand it over to the next shift.
            </p>
            <div className="sl-article-cta__actions">
              <a
                href="https://shiftlog.jarekborowiec.com"
                target="_blank"
                rel="noopener noreferrer"
                className="sl-btn sl-btn--primary sl-btn--lg"
                data-testid="article-cta-demo"
              >
                See ShiftLog Demo
                <ArrowUpRight size={18} strokeWidth={2.4} />
              </a>
              <a
                href="https://shiftlog.jarekborowiec.com"
                target="_blank"
                rel="noopener noreferrer"
                className="sl-btn sl-btn--ghost sl-btn--lg"
                data-testid="article-cta-request"
              >
                Request Demo
              </a>
            </div>
          </div>
        </section>

        <section className="sl-related" data-testid="related-section">
          <div className="sl-related__inner">
            <div className="sl-related__head">
              <h3 className="sl-related__title">Keep reading</h3>
              <a
                to="/resources"
                className="sl-related__all"
                data-testid="related-view-all"
              >
                View all articles →
              </a>
            </div>
            <div className="sl-related__grid">
              {related.map((a) => (
                <a
                  key={a.slug}
                  to={`/resources/${a.slug}`}
                  className="sl-related__card"
                  data-testid={`related-card-${a.slug}`}
                >
                  <div className="sl-related__meta">
                    {a.category} · {a.dateDisplay}
                  </div>
                  <div className="sl-related__t">{a.title}</div>
                  <div className="sl-related__cta">Read →</div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </article>
    </div>
  );
};

export default ShiftlogArticlePage;
